import { useDeferredValue, useMemo } from 'react';
import { parseAsInteger, parseAsString, useQueryStates } from 'nuqs';

/**
 * URL-backed `page` / `pageSize` / `q` state for a searchable, paginated
 * list, shared by the admin users and organizations pages.
 *
 * `page`, `pageSize`, and `q` reflect the URL immediately and drive the
 * controls. `request` is the same tuple passed through `useDeferredValue`, a
 * render-scheduling hint rather than a time-based debounce: React commits the
 * input keystroke first and lets the query key catch up in a background
 * render, so a burst of typing skips intermediate keys when the next keystroke
 * lands before the background render commits. Feed `request` to the query
 * hook so the page and search always change together in one request.
 *
 * A new search resets the page to 1 in the same URL update — staying on page
 * 7 after narrowing the result set to 3 rows would render "No results" until
 * the user paged back by hand. Deep links, reloads, and Back/Forward keep
 * whatever page the URL carries; only the user's own search edit resets it.
 */
export function useSearchPagination({ defaultPageSize }: { defaultPageSize: number }) {
  const [{ page, pageSize, q }, setParams] = useQueryStates({
    page: parseAsInteger.withDefault(1),
    pageSize: parseAsInteger.withDefault(defaultPageSize),
    q: parseAsString.withDefault(''),
  });

  const request = useDeferredValue(
    useMemo(() => ({ page, pageSize, q: q.trim() }), [page, pageSize, q]),
  );

  return {
    page,
    pageSize,
    q,
    request,
    setSearch: (value: string) =>
      void setParams({ q: value || null, page: 1 }, { history: 'replace' }),
    setPage: (next: number) => void setParams({ page: next }),
    setPageSize: (nextSize: number) => void setParams({ pageSize: nextSize, page: 1 }),
  };
}
